import React, { useState } from "react";
import {
  Wallet,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  RefreshCw,
  ExternalLink,
  AlertCircle,
  Copy,
  Link as LinkIcon,
  Zap,
  CloudOff,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  fetchPortfolio,
  isConfigured as zerionConfigured,
} from "../services/zerionApi";
import AffiliateCard from "./common/AffiliateCard";
import { AFFILIATE_LINKS } from "../config/affiliateLinks";

/**
 * Wallets Tab
 *
 * Users add/edit/delete multiple crypto wallet addresses. Each wallet is
 * stored in Firestore as part of the user's financial data document.
 *
 * Data model (single wallet entry):
 *   {
 *     id:         string (generated uuid-ish),
 *     label:      string (user-chosen nickname, e.g. "Cold Storage"),
 *     address:    string (0x... for EVM, base58 for Solana, etc.),
 *     chain:      "evm" | "solana" | "bitcoin" | "cosmos",
 *     notes:      string (optional),
 *     createdAt:  ISO date string,
 *     lastFetched: ISO date string | null,
 *     lastValue:   number | null  (cached total USD value from last refresh),
 *   }
 *
 * Data-fetch is intentionally NOT wired up yet — the UI is API-agnostic.
 * Plug in DeBank OpenAPI (Path A) or fallback (Zerion / Moralis) once chosen.
 */

/* ─── chain metadata ────────────────────────────────────────────────────── */
const CHAINS = [
  {
    id: "evm",
    label: "EVM",
    sublabel: "Ethereum · Base · Arbitrum · Optimism · Polygon · BSC",
    validate: (a) => /^0x[a-fA-F0-9]{40}$/.test(a.trim()),
    sample: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    explorer: (a) => `https://debank.com/profile/${a}`,
  },
  {
    id: "solana",
    label: "Solana",
    sublabel: "SOL · SPL tokens",
    // Rough Solana address validation — base58, 32–44 chars
    validate: (a) => /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(a.trim()),
    sample: "5U3EU2ubXtK84QcRjtPpwk9Qor3BEgCm2NUuDeWE6GgK",
    explorer: (a) => `https://solscan.io/account/${a}`,
  },
  {
    id: "bitcoin",
    label: "Bitcoin",
    sublabel: "BTC · native addresses",
    validate: (a) =>
      /^(bc1[a-z0-9]{8,87}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/.test(a.trim()),
    sample: "bc1qar0srrr7xfkvy5l643lydnw9re59gtzzwf5mdq",
    explorer: (a) => `https://mempool.space/address/${a}`,
  },
  {
    id: "cosmos",
    label: "Cosmos",
    sublabel: "ATOM · IBC chains",
    validate: (a) => /^cosmos1[a-z0-9]{38,}$/.test(a.trim()),
    sample: "cosmos1abc...",
    explorer: (a) => `https://www.mintscan.io/cosmos/account/${a}`,
  },
  {
    id: "ronin",
    label: "Ronin",
    sublabel: "RON · Axie ecosystem",
    // Ronin uses ronin: prefix or 0x EVM-style addresses
    validate: (a) =>
      /^(ronin:|0x)[a-fA-F0-9]{40}$/.test(a.trim().replace(/^ronin:/, "0x")),
    sample: "0x4FBd03e6E298a9C061528C7c2E36eb01B12395ec",
    explorer: (a) =>
      `https://app.roninchain.com/address/${a.replace(/^ronin:/, "")}`,
  },
];

// Chains where Zerion API can fetch real values
const ZERION_SUPPORTED_CHAINS = new Set(["evm"]);
// Chains that require user to type in the value manually
const MANUAL_ONLY_CHAINS = new Set(["solana", "bitcoin", "cosmos", "ronin"]);

const CHAIN_MAP = CHAINS.reduce((m, c) => ({ ...m, [c.id]: c }), {});

/* ─── helpers ──────────────────────────────────────────────────────────── */
const genId = () =>
  `w_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const shortAddr = (a) => {
  if (!a) return "";
  return a.length <= 14 ? a : `${a.slice(0, 6)}…${a.slice(-4)}`;
};

// Privacy mode — replaces visible chars with bullets so the address can't be
// read at a glance (e.g., when screen-sharing on a strategy call).
const maskedAddr = (a) => {
  if (!a) return "";
  if (a.length <= 14) return a.replace(/./g, "•");
  return "••••••••••••••";
};

const fmtCurrency = (n) =>
  n == null
    ? "—"
    : `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const fmtTimeAgo = (iso) => {
  if (!iso) return "Never";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

/* ─── Add-wallet form ──────────────────────────────────────────────────── */
const AddWalletForm = ({ onAdd, onCancel }) => {
  const [chain, setChain] = useState("evm");
  const [address, setAddress] = useState("");
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const chainDef = CHAIN_MAP[chain];
    const trimmed = address.trim();
    if (!trimmed) return setErr("Address is required.");
    if (!chainDef.validate(trimmed))
      return setErr(
        `That doesn't look like a valid ${chainDef.label} address.`
      );
    if (!label.trim()) return setErr("Give it a label so you can recognize it.");
    onAdd({
      id: genId(),
      label: label.trim(),
      address: trimmed,
      chain,
      notes: notes.trim(),
      createdAt: new Date().toISOString(),
      lastFetched: null,
      lastValue: null,
    });
    setErr("");
    setAddress("");
    setLabel("");
    setNotes("");
  };

  const selectedChain = CHAIN_MAP[chain];

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-arc-line bg-white/[0.02] p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="eyebrow mb-1">Add Wallet</p>
          <p className="text-arc-muted text-sm">
            Paste a public address to track. Read-only — we never ask for keys.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="btn-tactile p-2 rounded-lg text-arc-muted hover:text-arc-ink hover:bg-white/5"
          aria-label="Cancel"
        >
          <X size={16} />
        </button>
      </div>

      {/* Chain picker */}
      <div className="mb-4">
        <p className="eyebrow mb-2">Chain</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {CHAINS.map((c) => {
            const active = c.id === chain;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setChain(c.id);
                  setErr("");
                }}
                className={`btn-tactile p-3 rounded-xl border text-left transition-all ${
                  active
                    ? "bg-gradient-to-b from-arc-light/10 to-arc-deep/5 border-arc-deep text-arc-ink"
                    : "bg-white/[0.02] border-arc-line text-arc-muted hover:text-arc-ink hover:border-arc-line"
                }`}
              >
                <p className="font-medium text-sm">{c.label}</p>
                <p className="text-[10px] font-mono text-arc-dim mt-0.5 truncate">
                  {c.sublabel}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Address */}
      <label className="block mb-4">
        <span className="eyebrow block mb-2">Address</span>
        <input
          type="text"
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            if (err) setErr("");
          }}
          placeholder={selectedChain.sample}
          className="w-full px-3 py-2.5 rounded-xl text-sm num"
          spellCheck={false}
          autoComplete="off"
        />
      </label>

      {/* Label */}
      <label className="block mb-4">
        <span className="eyebrow block mb-2">Label</span>
        <input
          type="text"
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
            if (err) setErr("");
          }}
          placeholder="e.g. Cold Storage, Main Hot Wallet, DeFi Degens"
          className="w-full px-3 py-2.5 rounded-xl text-sm"
          maxLength={40}
        />
      </label>

      {/* Notes (optional) */}
      <label className="block mb-5">
        <span className="eyebrow block mb-2">Notes · optional</span>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Hardware wallet on Ledger"
          className="w-full px-3 py-2.5 rounded-xl text-sm"
          maxLength={120}
        />
      </label>

      {err && (
        <div className="mb-4 p-3 rounded-xl border border-arc-deep/40 bg-arc-deep/10 flex items-start gap-2.5">
          <AlertCircle className="text-arc-light flex-shrink-0 mt-0.5" size={16} />
          <span className="text-arc-light text-sm">{err}</span>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          className="btn-tactile btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2"
        >
          <Plus size={16} strokeWidth={2.5} />
          Add Wallet
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="btn-tactile btn-outline px-5 py-2.5 rounded-xl text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

/* ─── Effective value helper ───────────────────────────────────────────── */
// Manual value (user-entered) overrides API-fetched value when present.
// Used for chains we can't auto-fetch (Solana, Cosmos, Ronin) and as an
// optional override anywhere.
const effectiveValue = (w) =>
  typeof w.manualValue === "number" && !isNaN(w.manualValue)
    ? w.manualValue
    : w.lastValue || 0;

/* ─── Single wallet row ────────────────────────────────────────────────── */
const WalletRow = ({ wallet, onEdit, onRemove, onRefresh, onUpdateValue, refreshing, addressesHidden }) => {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(wallet.label);
  const [notes, setNotes] = useState(wallet.notes || "");
  const [editingValue, setEditingValue] = useState(false);
  const [valueInput, setValueInput] = useState(
    wallet.manualValue != null ? String(wallet.manualValue) : ""
  );
  const [copied, setCopied] = useState(false);

  const chainDef = CHAIN_MAP[wallet.chain] || CHAIN_MAP.evm;
  const isManualOnly = MANUAL_ONLY_CHAINS.has(wallet.chain);
  const value = effectiveValue(wallet);
  const usingManual = typeof wallet.manualValue === "number";

  const handleSave = () => {
    onEdit(wallet.id, {
      ...wallet,
      label: label.trim() || wallet.label,
      notes: notes.trim(),
    });
    setEditing(false);
  };

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(wallet.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  const handleSaveValue = () => {
    const parsed = parseFloat(valueInput);
    onUpdateValue(
      wallet.id,
      isNaN(parsed) || valueInput.trim() === "" ? null : parsed
    );
    setEditingValue(false);
  };

  const handleClearManual = () => {
    onUpdateValue(wallet.id, null);
    setValueInput("");
    setEditingValue(false);
  };

  return (
    <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4 hover:border-arc-line/50 transition-colors">
      <div className="flex items-start gap-4">
        {/* Chain badge */}
        <div className="flex-shrink-0 w-12 h-12 rounded-xl border border-arc-deep bg-gradient-to-br from-arc-light/15 to-arc-deep/5 flex items-center justify-center">
          <Wallet className="text-arc-gold" size={20} strokeWidth={1.75} />
        </div>

        {/* Identity + address */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="w-full px-2 py-1.5 rounded-lg text-sm"
                maxLength={40}
              />
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (optional)"
                className="w-full px-2 py-1.5 rounded-lg text-sm"
                maxLength={120}
              />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-arc-ink truncate">
                  {wallet.label}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-deep px-2 py-0.5 rounded-full border border-arc-line">
                  {chainDef.label}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="num text-xs text-arc-muted">
                  {addressesHidden ? maskedAddr(wallet.address) : shortAddr(wallet.address)}
                </span>
                <button
                  onClick={handleCopy}
                  className="btn-tactile p-1 rounded text-arc-dim hover:text-arc-gold"
                  aria-label="Copy address"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                </button>
                <a
                  href={chainDef.explorer(wallet.address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-tactile p-1 rounded text-arc-dim hover:text-arc-gold"
                  aria-label="View on explorer"
                >
                  <ExternalLink size={12} />
                </a>
              </div>
              {wallet.notes && (
                <p className="text-xs text-arc-dim mt-1.5 italic">
                  {wallet.notes}
                </p>
              )}
            </>
          )}
        </div>

        {/* Value + actions */}
        <div className="flex-shrink-0 text-right">
          {editingValue ? (
            <div className="flex items-center gap-1">
              <span className="text-arc-dim text-sm">$</span>
              <input
                type="number"
                value={valueInput}
                onChange={(e) => setValueInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveValue();
                  if (e.key === "Escape") {
                    setEditingValue(false);
                    setValueInput(
                      wallet.manualValue != null
                        ? String(wallet.manualValue)
                        : ""
                    );
                  }
                }}
                autoFocus
                placeholder="0"
                className="w-32 px-2 py-1 rounded-md text-sm num text-right"
              />
              <button
                onClick={handleSaveValue}
                className="btn-tactile p-1 text-arc-gold hover:text-arc-light"
                aria-label="Save value"
              >
                <Check size={14} strokeWidth={2.5} />
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={() => setEditingValue(true)}
                className="num text-lg text-arc-ink hover:text-arc-gold transition-colors text-right"
                title="Click to edit value manually"
              >
                {fmtCurrency(value)}
              </button>
              <p className="text-[10px] font-mono uppercase tracking-[0.12em] text-arc-dim mt-0.5">
                {usingManual
                  ? "Manual"
                  : isManualOnly
                  ? "Tap value to edit"
                  : fmtTimeAgo(wallet.lastFetched)}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-arc-line">
        {editing ? (
          <>
            <button
              onClick={handleSave}
              className="btn-tactile btn-gold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5"
            >
              <Check size={12} strokeWidth={2.5} />
              Save
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setLabel(wallet.label);
                setNotes(wallet.notes || "");
              }}
              className="btn-tactile btn-outline px-3 py-1.5 rounded-lg text-xs"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            {!isManualOnly && (
              <button
                onClick={() => onRefresh(wallet.id)}
                disabled={refreshing}
                className="btn-tactile btn-outline px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50"
              >
                <RefreshCw
                  size={12}
                  className={refreshing ? "animate-spin" : ""}
                />
                Refresh
              </button>
            )}
            <button
              onClick={() => setEditingValue(true)}
              className="btn-tactile btn-outline px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5"
            >
              <Edit2 size={12} />
              {isManualOnly ? "Set Value" : "Override"}
            </button>
            {usingManual && !isManualOnly && (
              <button
                onClick={handleClearManual}
                className="btn-tactile px-3 py-1.5 rounded-lg text-xs text-arc-dim hover:text-arc-light"
                title="Clear manual value, use API value instead"
              >
                Reset
              </button>
            )}
            <a
              href={chainDef.explorer(wallet.address)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-tactile btn-outline px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5"
            >
              <LinkIcon size={12} />
              {wallet.chain === "evm" ? "DeBank" : "Explorer"}
            </a>
            <button
              onClick={() => setEditing(true)}
              className="btn-tactile ml-auto p-1.5 rounded-lg text-arc-muted hover:text-arc-ink hover:bg-white/5"
              aria-label="Edit wallet"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Remove wallet "${wallet.label}"?`))
                  onRemove(wallet.id);
              }}
              className="btn-tactile p-1.5 rounded-lg text-arc-muted hover:text-red-400 hover:bg-red-500/10"
              aria-label="Remove wallet"
            >
              <Trash2 size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/* ─── Main WalletsTab ──────────────────────────────────────────────────── */
const WalletsTab = ({ wallets = [], setWallets }) => {
  const [adding, setAdding] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshingIds, setRefreshingIds] = useState(new Set());
  const [refreshError, setRefreshError] = useState(null);
  const [addressesHidden, setAddressesHidden] = useState(false);

  const liveMode = zerionConfigured();
  const totalValue = wallets.reduce((s, w) => s + effectiveValue(w), 0);
  const trackedChains = new Set(wallets.map((w) => w.chain));

  const handleAdd = (newWallet) => {
    setWallets([...wallets, newWallet]);
    setAdding(false);
  };

  const handleEdit = (id, updatedWallet) => {
    setWallets(wallets.map((w) => (w.id === id ? updatedWallet : w)));
  };

  const handleUpdateValue = (id, manualValue) => {
    setWallets(
      wallets.map((w) =>
        w.id === id
          ? {
              ...w,
              manualValue,
              // When manually set, also stamp lastFetched so timeago is fresh
              ...(manualValue != null
                ? { lastFetched: new Date().toISOString() }
                : {}),
            }
          : w
      )
    );
  };

  const handleRemove = (id) => {
    setWallets(wallets.filter((w) => w.id !== id));
  };

  /* ─── Refresh — calls Zerion if API key is configured, else mock data ── */
  const handleRefresh = async (id) => {
    const wallet = wallets.find((w) => w.id === id);
    if (!wallet) return;

    setRefreshingIds((s) => new Set(s).add(id));
    setRefreshError(null);

    try {
      // Zerion's portfolio endpoint covers EVM chains only.
      // Solana, Bitcoin, and Cosmos require separate APIs (Helius for Solana,
      // mempool.space for BTC, etc.) — TODO for v1.5. For now, skip the
      // Zerion call gracefully and let the user view via the explorer link.
      const unsupportedChains = new Set(["solana", "bitcoin", "cosmos"]);
      if (unsupportedChains.has(wallet.chain)) {
        setWallets((curr) =>
          curr.map((w) =>
            w.id === id
              ? {
                  ...w,
                  lastFetched: new Date().toISOString(),
                  lastValue: w.lastValue,
                  unsupported: true,
                }
              : w
          )
        );
        setRefreshError(
          `${wallet.label}: ${wallet.chain.toUpperCase()} not yet supported by our data provider. Click the explorer link on the wallet to view it externally.`
        );
        return;
      }

      // Only fetch portfolio total — positions are heavy and most users
      // only care about the aggregate value. We can add a "View Details"
      // action later that fetches positions on demand without hammering
      // the rate limit on every refresh.
      const portfolio = await fetchPortfolio(wallet.address, { force: true });

      setWallets((curr) =>
        curr.map((w) =>
          w.id === id
            ? {
                ...w,
                lastFetched: new Date().toISOString(),
                lastValue: portfolio.totalValue,
                isMock: portfolio.isMock || false,
                unsupported: false,
              }
            : w
        )
      );
    } catch (err) {
      console.error(`Refresh failed for wallet ${id}:`, err);
      setRefreshError(err.message || "Refresh failed.");
    } finally {
      setRefreshingIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  const handleRefreshAll = async () => {
    setRefreshingAll(true);
    setRefreshError(null);
    try {
      // Sequential with small inter-wallet pause to respect free-tier
      // per-second throttling (Zerion free tier = 3000/day with bursty limits).
      for (let i = 0; i < wallets.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        await handleRefresh(wallets[i].id);
        if (i < wallets.length - 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 1100));
        }
      }
    } finally {
      setRefreshingAll(false);
    }
  };

  return (
    <div className="surface p-6 sm:p-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <p className="eyebrow mb-2">On-chain</p>
          <h2 className="headline text-2xl mb-2">Wallets</h2>
          <p className="text-arc-muted text-sm leading-relaxed max-w-2xl">
            Track your crypto across every wallet you own — read-only, non-custodial.
            Paste public addresses, label them, and see your full DeFi picture in one place.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {wallets.length > 0 && (
            <button
              onClick={() => setAddressesHidden(!addressesHidden)}
              className="btn-tactile btn-outline px-4 py-2 rounded-xl text-sm flex items-center gap-2"
              title={addressesHidden ? "Show wallet addresses" : "Hide wallet addresses (privacy mode)"}
            >
              {addressesHidden ? <EyeOff size={14} /> : <Eye size={14} />}
              {addressesHidden ? "Show" : "Hide"} Addresses
            </button>
          )}
          {wallets.length > 0 && (
            <button
              onClick={handleRefreshAll}
              disabled={refreshingAll || wallets.length === 0}
              className="btn-tactile btn-outline px-4 py-2 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw
                size={14}
                className={refreshingAll ? "animate-spin" : ""}
              />
              Refresh All
            </button>
          )}
          {!adding && (
            <button
              onClick={() => setAdding(true)}
              className="btn-tactile btn-gold px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"
            >
              <Plus size={16} strokeWidth={2.5} />
              Add Wallet
            </button>
          )}
        </div>
      </div>

      {/* Live/Mock data source indicator */}
      <div
        className={`mb-6 rounded-xl border px-4 py-2.5 flex items-center gap-2.5 text-xs ${
          liveMode
            ? "border-arc-deep/40 bg-arc-deep/5 text-arc-light"
            : "border-arc-line bg-white/[0.02] text-arc-muted"
        }`}
      >
        {liveMode ? <Zap size={14} /> : <CloudOff size={14} />}
        <span className="font-mono uppercase tracking-[0.14em]">
          {liveMode ? "Live · Zerion API" : "Demo data · No API key"}
        </span>
        {!liveMode && (
          <span className="text-arc-dim normal-case font-sans tracking-normal ml-auto">
            Add REACT_APP_ZERION_API_KEY to .env.local for real balances
          </span>
        )}
      </div>

      {/* Refresh error banner */}
      {refreshError && (
        <div className="mb-6 rounded-xl border border-arc-deep/40 bg-arc-deep/10 p-3 flex items-start gap-2.5">
          <AlertCircle className="text-arc-light flex-shrink-0 mt-0.5" size={16} />
          <span className="text-arc-light text-sm flex-1">{refreshError}</span>
          <button
            onClick={() => setRefreshError(null)}
            className="text-arc-muted hover:text-arc-ink"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Summary */}
      {wallets.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4">
            <p className="eyebrow mb-1">Total Crypto Value</p>
            <p className="text-2xl num text-arc-gold">{fmtCurrency(totalValue)}</p>
          </div>
          <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4">
            <p className="eyebrow mb-1">Wallets Tracked</p>
            <p className="text-2xl num text-arc-ink">{wallets.length}</p>
          </div>
          <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4">
            <p className="eyebrow mb-1">Chains</p>
            <p className="text-2xl num text-arc-ink">{trackedChains.size}</p>
          </div>
        </div>
      )}

      {/* Add form */}
      {adding && (
        <div className="mb-6">
          <AddWalletForm
            onAdd={handleAdd}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      {/* Wallet list / empty state */}
      {wallets.length === 0 && !adding ? (
        <div className="rounded-2xl border border-dashed border-arc-line bg-white/[0.02] p-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl border border-arc-deep bg-gradient-to-br from-arc-light/10 to-arc-deep/5 mb-4">
            <Wallet className="text-arc-gold" size={28} strokeWidth={1.5} />
          </div>
          <p className="eyebrow mb-2">No wallets yet</p>
          <h3 className="headline text-xl mb-3">
            Add your <span className="text-arc-gold">first wallet</span>
          </h3>
          <p className="text-arc-muted text-sm mb-5 max-w-md mx-auto">
            Start by pasting a public address. We'll read balances from the chain —
            your keys never touch our servers.
          </p>
          <button
            onClick={() => setAdding(true)}
            className="btn-tactile btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2"
          >
            <Plus size={16} strokeWidth={2.5} />
            Add Wallet
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {wallets.map((w) => (
            <WalletRow
              key={w.id}
              wallet={w}
              onEdit={handleEdit}
              onRemove={handleRemove}
              onRefresh={handleRefresh}
              onUpdateValue={handleUpdateValue}
              refreshing={refreshingIds.has(w.id)}
              addressesHidden={addressesHidden}
            />
          ))}
        </div>
      )}

      {/* Footer note */}
      {wallets.length > 0 && (
        <p className="mt-6 text-center text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim">
          Read-only · Public addresses · Non-custodial · Your keys stay with you
        </p>
      )}

      {/* Contextual recommendation: Ledger hardware wallet — high-priority
          card. Wallets tab is the perfect surface — users here are already
          thinking about self-custody. */}
      {(() => {
        const ledger = AFFILIATE_LINKS.find((l) => l.id === "ledger");
        if (!ledger) return null;
        return (
          <div className="mt-6 pt-5 border-t border-arc-line">
            <p className="eyebrow mb-3">Recommended · self-custody</p>
            <AffiliateCard link={ledger} medium="wallets-tab" />
          </div>
        );
      })()}
    </div>
  );
};

export default WalletsTab;
